import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Vault, Sparkle, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AiProgressBanner } from '@/components/AiProgressBanner';
import { useT } from '@/i18n/LanguageContext';
import { plannerApi } from '../plannerApi';

const splitList = (s) => (s || '').split(/[、,]/).map((x) => x.trim()).filter(Boolean);

export const EMPTY_FORM = { name: '', date_start: '', date_end: '', people: '', department_text: '', tags: '', summary: '' };

export const formToBody = (form, method) => ({
  name: (form.name || '').trim(),
  date_start: form.date_start || null,
  date_end: form.date_end || null,
  people: splitList(form.people),
  department_text: (form.department_text || '').trim(),
  tags: splitList(form.tags),
  summary: (form.summary || '').trim(),
  ...(method ? { extraction_method: method } : {}),
});

export const fieldsToForm = (f) => ({
  name: f.name || '',
  date_start: f.date_start || '',
  date_end: f.date_end || '',
  people: (f.people || []).join('、'),
  department_text: f.department_text || '',
  tags: (f.tags || []).join(', '),
  summary: f.summary || '',
});

export function OldProjectFields({ form, setForm, t }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-semibold text-zinc-600">{t('planner.oldproj.name')} *</label>
        <Input value={form.name} onChange={set('name')} className="mt-1" data-testid="oldproj-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-zinc-600">{t('planner.oldproj.dateStart')}</label>
          <Input type="date" value={form.date_start} onChange={set('date_start')} className="mt-1" data-testid="oldproj-date-start-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-zinc-600">{t('planner.oldproj.dateEnd')}</label>
          <Input type="date" value={form.date_end} onChange={set('date_end')} className="mt-1" data-testid="oldproj-date-end-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-zinc-600">{t('planner.oldproj.people')}</label>
          <Input value={form.people} onChange={set('people')} placeholder={t('planner.oldproj.peopleHint')} className="mt-1" data-testid="oldproj-people-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-zinc-600">{t('planner.oldproj.department')}</label>
          <Input value={form.department_text} onChange={set('department_text')} className="mt-1" data-testid="oldproj-dept-input" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-zinc-600">{t('planner.oldproj.tags')}</label>
        <Input value={form.tags} onChange={set('tags')} placeholder={t('planner.oldproj.tagsHint')} className="mt-1" data-testid="oldproj-tags-input" />
      </div>
      <div>
        <label className="text-xs font-semibold text-zinc-600">{t('planner.oldproj.summary')}</label>
        <Textarea value={form.summary} onChange={set('summary')} rows={4} className="mt-1 text-sm" data-testid="oldproj-summary-input" />
      </div>
    </div>
  );
}

export default function CreateOldProjectDialog({ rec, onClose, onCreated }) {
  const { t } = useT();
  const [loading, setLoading] = useState(false);
  const [loadingSince, setLoadingSince] = useState(null);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showExcerpt, setShowExcerpt] = useState(false);

  useEffect(() => {
    if (!rec) return;
    setForm(EMPTY_FORM); setMethod(null); setShowExcerpt(false); setLoading(true);
    setLoadingSince(new Date().toISOString());
    plannerApi.archiveExtractOldProject(rec.id).then((d) => {
      setForm(fieldsToForm(d.fields || {}));
      setMethod(d.extraction_method);
    }).catch((e) => toast.error(e?.response?.data?.detail || t('planner.error')))
      .finally(() => { setLoading(false); setLoadingSince(null); });
  }, [rec]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!(form.name || '').trim()) { toast.error(`${t('planner.oldproj.name')}は必須です`); return; }
    setBusy(true);
    try {
      await plannerApi.archiveCreateOldProject(rec.id, formToBody(form, method));
      toast.success(t('planner.oldproj.created'));
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
    setBusy(false);
  };

  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="oldproj-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Vault size={18} weight="duotone" className="text-violet-600" /> {t('planner.oldproj.create')}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-6 space-y-3" data-testid="oldproj-extracting">
            <div className="text-center space-y-1">
              <Sparkle size={22} className="mx-auto text-violet-500 animate-pulse" />
              <p className="text-sm text-zinc-500">{t('planner.oldproj.extracting')}</p>
            </div>
            <AiProgressBanner
              steps={['アーカイブ本文を読み込み中…', 'AIで日付・関係者を抽出中…', 'キーワード・要約を推定中…', 'フォームに反映中…']}
              since={loadingSince}
              tone="violet"
              defaultLabel="AIが旧プロジェクトの情報を抽出しています…"
              footer="Odooから取り込んだアーカイブ本文をAIが解析し、名称・期間・担当者・要約を推定します（通常 10〜45 秒）。"
              testIdPrefix="oldproj-progress"
              simulatedStepMs={8000}
            />
          </div>
        ) : (
          <div className="space-y-3">
            {method && (
              <Badge variant="outline" data-testid="oldproj-method-badge"
                className={method === 'ai' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-zinc-50 text-zinc-600 border-zinc-200'}>
                {method === 'ai' && <Sparkle size={11} className="mr-1" />}{t(`planner.oldproj.method.${method}`)}
              </Badge>
            )}
            <OldProjectFields form={form} setForm={setForm} t={t} />
            {rec?.raw_excerpt && (
              <div>
                <button type="button" onClick={() => setShowExcerpt((v) => !v)} data-testid="oldproj-excerpt-toggle"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700">
                  {showExcerpt ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />} {t('planner.oldproj.sourceExcerpt')}
                </button>
                {showExcerpt && <pre className="mt-1 p-2 rounded bg-zinc-50 border border-zinc-200 text-[11px] text-zinc-600 whitespace-pre-wrap max-h-48 overflow-y-auto">{rec.raw_excerpt}</pre>}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('planner.cancel')}</Button>
          <Button onClick={submit} disabled={loading || busy} data-testid="oldproj-create-save-btn" className="bg-teal-700 hover:bg-teal-800">
            {t('planner.oldproj.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
